import { useEffect, useRef, useState } from 'react'
import { ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TONES, TONE_ORDER, type ListingTone } from './tones'

interface BadgeMarqueeProps {
  /** Badge labels, shown as given. Short lists are repeated to fill the row. */
  items: string[]
  /** Accessible name for the static list behind the moving row. */
  label: string
  tones?: ListingTone[]
  className?: string
}

/**
 * One row of bordered badge tiles scrolling sideways. The moving row is
 * decorative; screen readers get a plain list. Pauses off-screen and on
 * hover; static under reduced motion (global rule stops .animate-marquee).
 */
export function BadgeMarquee({ items, label, tones = TONE_ORDER, className }: BadgeMarqueeProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const io = new IntersectionObserver(([entry]) => setInView(!!entry?.isIntersecting))
    io.observe(el)
    return () => io.disconnect()
  }, [])

  // Repeat short lists so one copy is wider than the widest viewport.
  const copies = Math.max(1, Math.ceil(8 / Math.max(items.length, 1)))
  const row = Array.from({ length: copies }, () => items).flat()

  const renderRow = (key: string) => (
    <div key={key} className="flex shrink-0 gap-3 pr-3">
      {row.map((item, i) => {
        const tone = TONES[tones[i % tones.length]!]
        return (
          <div
            key={`${key}-${i}`}
            className="flex h-20 shrink-0 items-center gap-4 border border-[var(--color-landing-gray-200)] bg-white px-5 dark:border-[var(--color-landing-gray-800)] dark:bg-[#12121a] md:h-24 md:px-7"
          >
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center"
              style={{ backgroundColor: tone.bg, color: tone.fg }}
            >
              <ShieldCheck className="h-5 w-5" />
            </span>
            <span className="whitespace-nowrap font-[family-name:var(--font-landing-subdisplay)] text-sm font-bold uppercase tracking-[0.02em] text-[var(--color-landing-gray-900)] dark:text-white md:text-base">
              {item}
            </span>
          </div>
        )
      })}
    </div>
  )

  return (
    <div ref={rootRef} className={cn('relative overflow-hidden', className)}>
      <ul className="sr-only" aria-label={label}>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-white to-transparent dark:from-[#0a0a0f] md:w-24" />
      <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-white to-transparent dark:from-[#0a0a0f] md:w-24" />
      <div
        aria-hidden
        className="animate-marquee flex w-max"
        style={{ animationPlayState: inView ? undefined : 'paused', animationDuration: `${row.length * 5}s` }}
      >
        {renderRow('a')}
        {renderRow('b')}
      </div>
    </div>
  )
}
